/**
 * MarkdownView — split-pane markdown editor with live preview.
 *
 * Edit mode ON (input panel): left textarea + right rendered preview.
 * Edit mode OFF (output panel): full-width rendered preview only.
 *
 * Preview rendering is debounced for performance; onEdit fires on every
 * keystroke to keep the pipeline source of truth current.
 */

import DOMPurify from 'dompurify';
import { marked } from 'marked';
import React, { useCallback, useEffect, useMemo,useRef, useState } from 'react';

import { cn } from '@/lib/utils';

interface MarkdownViewProps {
  data: string;
  editMode: boolean;
  onEdit?: (newData: string) => void;
  breaks: boolean;
}

const DEBOUNCE_MS = 200;

function renderMarkdown(input: string, breaks: boolean): string {
  const rawHtml = String(marked.parse(String(input ?? ''), { gfm: true, breaks }) ?? '');
  return DOMPurify.sanitize(rawHtml);
}

const PROSE_CLASSES = `prose prose-sm dark:prose-invert max-w-none
  prose-headings:text-foreground
  prose-p:text-foreground
  prose-strong:text-foreground
  prose-a:text-primary hover:prose-a:text-primary/80
  prose-blockquote:border-border prose-blockquote:text-muted-foreground
  prose-code:text-foreground prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded-sm prose-code:before:content-none prose-code:after:content-none
  prose-pre:bg-muted prose-pre:text-foreground
  prose-th:text-foreground prose-td:text-foreground
  prose-th:border-border prose-td:border-border
  prose-hr:border-border
  prose-li:text-foreground
  prose-li:marker:text-muted-foreground`;

export function MarkdownView({ data, editMode, onEdit, breaks }: MarkdownViewProps) {
  const [draft, setDraft] = useState(data);
  const [previewSource, setPreviewSource] = useState(data);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);

  // Sync external prop changes into local draft (but not while user is typing)
  useEffect(() => {
    if (!isTypingRef.current) {
      setDraft(data);
      setPreviewSource(data);
    }
  }, [data]);

  // Clear typing flag after a pause (so external updates can flow in)
  useEffect(() => {
    if (!isTypingRef.current) return;
    const timer = setTimeout(() => { isTypingRef.current = false; }, DEBOUNCE_MS + 100);
    return () => clearTimeout(timer);
  }, [draft]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    isTypingRef.current = true;
    setDraft(value);
    onEdit?.(value);

    // Debounce the expensive markdown parse
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPreviewSource(value);
    }, DEBOUNCE_MS);
  }, [onEdit]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const html = useMemo(() => renderMarkdown(previewSource, breaks), [previewSource, breaks]);

  // Preview-only mode (output panel)
  if (!editMode) {
    return (
      <div className="h-full overflow-auto p-4 text-sm">
        <div
          className={PROSE_CLASSES}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(data, breaks) }}
        />
      </div>
    );
  }

  // Split editor+preview mode (input panel)
  return (
    <div className="flex h-full min-h-0">
      {/* Raw editor */}
      <div className="flex flex-col w-1/2 min-w-0 border-r border-border">
        <div className="flex items-center px-3 py-1.5 border-b border-border bg-muted/30">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Markdown
          </span>
        </div>
        <textarea
          className={cn(
            'flex-1 w-full resize-none p-3 text-sm font-mono',
            'bg-background text-foreground',
            'border-none outline-none focus:ring-0',
            'placeholder:text-muted-foreground/50',
          )}
          value={draft}
          onChange={handleChange}
          spellCheck={false}
          placeholder="Type markdown here…"
        />
      </div>

      {/* Rendered preview */}
      <div className="flex flex-col w-1/2 min-w-0">
        <div className="flex items-center px-3 py-1.5 border-b border-border bg-muted/30">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Preview
          </span>
        </div>
        <div className="flex-1 overflow-auto p-4 text-sm">
          <div
            className={PROSE_CLASSES}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </div>
    </div>
  );
}
